/**
 * How many entries deep into the app the member currently is.
 *
 * `window.history.length` cannot answer this. It counts entries ever created and never
 * shrinks when you go back, and it also counts whatever the tab did *before* the app
 * loaded — so on a deep link into a shared article it is already greater than one, and
 * "go back" walks the member out of the app entirely.
 *
 * Counting the router's own PUSH/POP transitions gives the real answer. The counter is
 * module state rather than React state because nothing needs to re-render when it
 * changes: it is read at the moment a back control is pressed.
 */
let depth = 0;

/**
 * The three router transitions.
 *
 * Declared here rather than imported: react-router models these as a string *enum*
 * (`Action`), whose members are not assignable from plain string literals — which would
 * make this module awkward to call from a test.
 */
export type NavigationKind = "PUSH" | "POP" | "REPLACE";

/** Fold one router transition into the counter. */
export function recordNavigation(type: NavigationKind): void {
  if (type === "PUSH") {
    depth += 1;
  } else if (type === "POP") {
    depth = Math.max(0, depth - 1);
  }
  // REPLACE swaps the current entry, so the depth is unchanged.
}

/** Whether there is an in-app entry to return to. */
export function canGoBack(): boolean {
  return depth > 0;
}

/** Reset the counter. Used by tests and after a sign-out clears the session. */
export function resetNavigationDepth(): void {
  depth = 0;
}
